import {
    notificationCaseSchema,
    notificationNoteSchema,
    notificationSchema,
} from "@/components/Forms/schema/notificationSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    handleJsonApiError,
} from "jsonapi-zod-query";

const resourceSelector = createResourceCollectionSelector({
    type: "notification",
    attributesSchema: notificationSchema,
    relationships: {
        case: {
            relationshipType: "one",
            include: {
                type: "case",
                attributesSchema: notificationCaseSchema,
            },
        },
        note: {
            relationshipType: "one",
            include: {
                type: "note",
                attributesSchema: notificationNoteSchema,
            },
        },
    },
});

export const notificationsSelector = createDataSelector(resourceSelector);
export type Notifications = ReturnType<typeof notificationsSelector>;
export type Notification = Notifications[number];

export const useFetchNotificationsForUser = (): UseQueryResult<Notifications> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case-notifications"],
        queryFn: async () => {
            const response = await fetch(apiUrl("/notification/for-current-user"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            await handleJsonApiError(response);

            return await response.json();
        },
        select: notificationsSelector,
        staleTime: 30_000, // 30 seconds - notifications change more frequently
        refetchInterval: 60_000, // Poll every 60 seconds for new notifications
        refetchOnWindowFocus: true, // Refetch when user returns to window
        refetchOnMount: false, // Use cached data on mount
    });
};
